import { formatDistance } from "date-fns";
import { es } from 'date-fns/locale';
import { useCallback, useState, useContext } from 'react';
import { HiOutlineEye } from "react-icons/hi2";
import { MdLockReset, MdOutlineModeEditOutline, MdOutlineSettings } from "react-icons/md";
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ActionIcon, Loader, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { RootState } from '../../../redux/store';
import { menuRoutes } from '../../../router/menu';
import { UserService } from '../../../services/user/userService';
import { UsersApiResponse } from '../../../type/entities/user-type';
import UsersFilters from './UsersFilters';
import { PrivilegeContext } from '../../../components/priviledge/PriviledgeProvider';
import { IoMdLogIn } from "react-icons/io";
import { login, User } from "../../../redux/authSlice";

const columnOrder = [
    'id',
    'profileImg',
    'name',
    'lastName',
    'dni',
    'email',
    'phone',
    'userRoles',
    'active',
    'lastLogin',
    'createdAt',
    'updatedAt',
];

const UsersList = () => {

    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const service = new UserService();

    // HOOKS

    const { user } = useSelector((state: RootState) => state.auth);
    const { handleErrors } = useHandleErrors();
    const dispatch = useDispatch();
    const { userCan  } = useContext(PrivilegeContext);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA
    
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE USUARIOS
     * @EN GETS THE LIST OF USERS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error,refetch] = useFetch(useCallback(async () => {
        const response = await service.listUsers(filters);
        return response.getResponseData() as UsersApiResponse;
    }, [filters]));
    // -------------------------------------------------------------------------------------------------------------

    // FUNCTIONS
    
    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES TOGGLE STATUS DEL USUARIO
     * @EN TOGGLE USER STATUS
     */
    //-------------------------------------------------------------------------------------------------------------
    const toggleStatus = async (id: string, status: boolean) => {
        try {
            setChangingStatus([...changingStatus, id]);
            const response = (await service.toggleUserStatus(id, status)).getResponseData();
            if (response.success) {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                refetch();
                toast.success(status ? "Se ha desactivado el usuario" : "Se ha activado el usuario");
            } else {
                setChangingStatus(changingStatus.filter((item) => item !== id));
                toast.error(response.message);
            }
        } catch (error: any) {
            setChangingStatus(changingStatus.filter((item) => item !== id));
            toast.error(error.message);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES INICIAR SESIÓN EN LA PLATAFORMA CON EL USUARIO SELECCIONADO.
     * @EN LOGIN TO THE PLATFORM AS THE SELECTED USER.
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleLoginAsUser = async (id: string) => {
        setIsLoading(true);
        const response = await service.loginAsUser(id) as any;
        const responseData = response.getResponseData();

        if (response && responseData?.success) {
            try {
                const user: User = {
                    id: responseData.data.user.id,
                    email: responseData.data.user.email,
                    token: responseData.data.token,
                    name: responseData.data.user.name,
                    lastName: responseData.data.user.lastName,
                    profileImage: responseData.data.user.imgProfile,
                    roles: responseData.data.user.roles,
                    companyName: responseData.data.user.companyName,
                };
                dispatch(login({
                    isAuthenticated: true,
                    loading: false,
                    error: null,
                    user: user
                }))
                toast.success('Has accedido como ' + user.name);

            } catch (error) {
                handleErrors(error);
                return;
            } finally {
                setIsLoading(false);
            }
        } else {
            handleErrors(responseData);
        }
    }
    //-------------------------------------------------------------------------------------------------------------
   
    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE UN USUARIO
     * @EN HANDLES THE DELETION OF A USER
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteUser(id)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuario eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELMINACIÓN DE VARIOS USUARIOS
     * @EN HANDLES THE DELETION OF MULTIPLE USERS
     */
    //-------------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = await (await service.deleteMultiUsers(ids)).getResponseData();
            if (response.success) {
                refetch();
                toast.success('Usuarios eliminados correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------
    
    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES FORMATEA EL NOMBRE DE LA COLUMNA
     * @EN FORMATS THE COLUMN NAME
     */
    //-------------------------------------------------------------------------------------------------------------
    const capitalize = (str: string) => {
        if (typeof str !== 'string') return '';
        return str.charAt(0).toUpperCase() + str.slice(1);
    };
    //-------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------
    
    // RENDER

    return (
        <Page container='fluid'>
            {data !== undefined && (
                <CustomTable
                    id={'users-table'}
                    divClassName='max-h-table'
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={['active', 'profileImg']}
                    columnsNotShown={['lastName', 'updatedAt']}
                    overrideColumns={[
                        {
                            key: 'name',
                            render: (row: any) => {
                                const value = row.row.original.name;
                                let name = value;
                                if (row.row.original.lastName) {
                                    name = `${value} ${row.row.original.lastName}`;
                                }
                                if (userCan('get', 'user')) {
                                    return (
                                        <a href={`/users/${row.row.original.id}/profile`} className='text-primary hover:text-primary hover:underline'>
                                            {name}
                                        </a>
                                    );
                                } else {
                                    return <span>{name}</span>;
                                }
                            }
                        },
                        {
                            key: 'userRoles',
                            render: (row: any) => {
                                return row.row.original.userRoles.map((role: any) => role.role.name).join(', ')
                            }
                        },
                        {
                            key: 'lastLogin',
                            render: (row: any) => {
                                const date = row.row.original?.lastLogin?.date;
                                return date ? capitalize(formatDistance(new Date(date), new Date(), { addSuffix: true, locale: es })) : 'Nunca';
                            },
                        },
                        {
                            key: 'active',
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={row.row.original.active ? 'Desactivar usuario' : 'Activar usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Switch
                                            id={row.row.original.id}
                                            checked={row.row.original.active}
                                            disabled={changingStatus.includes(row.row.original.id) || row.row.original.id === user?.id || !userCan('edit', 'user')}
                                            onChange={() => toggleStatus(row.row.original.id, row.row.original.active)}
                                            switchKnobClassName='bg-red-300'
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Log in',
                            permissions: { group: 'user', permission: 'login_as_user' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Acceder como usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            onClick={() => handleLoginAsUser(row.id)}
                                            disabled={isLoading}
                                        >
                                            {isLoading ? <Loader /> :
                                                <IoMdLogIn className="h-3 w-3" size={20} />}
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            }
                        },
                        {
                            label: 'View',
                            permissions: { group: 'user', permission: 'get' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Ver perfil'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link
                                            to={`${menuRoutes.users.path}/${row.id}/profile/${menuRoutes.users.profile.info}`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <HiOutlineEye className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Reset',
                            permissions: { group: 'user', permission: 'edit_user_permissions' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Resetear permisos'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={async () => {
                                                try {
                                                    const result = await service.resetPermissions(row.id);
                                                    if (result.getResponseData().success) {
                                                        toast.success("Permisos reseteados correctamente");
                                                    } else {
                                                        toast.error("Error al resetear los permisos");
                                                    }
                                                } catch (error) {
                                                    handleErrors(error);
                                                }
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                        >
                                            <MdLockReset className="h-3 w-3" size={20} />
                                        </ActionIcon>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'user', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.users.path}/${row.id}/edit/info`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Edit',
                            permissions: { group: 'user', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Editar Permisos del usuario'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <Link to={`${menuRoutes.users.path}/${row.id}/edit/permissions`}>
                                            <ActionIcon
                                                as="span"
                                                size="sm"
                                                variant="outline"
                                                className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                            >
                                                <MdOutlineSettings className="h-3 w-3" size={20} />
                                            </ActionIcon>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'user', permission: 'delete' },
                            render: (row: any) => {
                                if (row.id === user?.id) return null;
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                actionIconClassName="w-5 h-5"
                                                title={`Eliminar usuario`}
                                                description={`¿Estás seguro de que deseas eliminar a ${row.name}?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as UsersApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Usuarios'}>
                <UsersFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>
        </Page>
    );
};

export default UsersList;